import { isLeafletMapUsable, stopLeafletMap } from 'components/BaseMap/util/BaseMap.utils';
import { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';

const hasRenderedSize = (map) => {
  const size = map.getSize();
  return size.x > 0 && size.y > 0;
};

// Without a fit key, the component fits the first bounds once for each map instance.
const FIT_ONCE = Symbol('fit-once');

// Given a pair of coordinates, this component sets the bounds of the map to fit the coordinates.
// It only performs that fit once per map instance so draft updates do not keep zooming and
// recentering a user while they are editing an IPAWS area.
//
// A caller that must fit again gives a `fitKey` (a fit request). The component fits once for each
// new key, when the bounds for that key are known. New bounds with the same key do not fit again,
// so shape edits never move the map. `padding` (pixels) keeps the boundary clear of the map edge.
export default function BoundsSetter({ bounds, fitKey, padding }) {
  const map = useMap();
  const appliedRequestRef = useRef(null);
  const request = fitKey === undefined ? FIT_ONCE : fitKey;

  useEffect(() => {
    if (!isLeafletMapUsable(map) || !bounds || appliedRequestRef.current === request) {
      return undefined;
    }

    const fitToBounds = () => {
      stopLeafletMap(map); // stop any ongoing map animations before setting new bounds
      map.fitBounds(
        bounds,
        padding ? { animate: false, padding: [padding, padding] } : { animate: false },
      );
      appliedRequestRef.current = request;
    };

    if (hasRenderedSize(map)) {
      fitToBounds();
      return undefined;
    }

    // The container has no size yet (a dashboard widget before layout, a hidden host). Fitting now
    // makes Leaflet clamp the zoom to its minimum and show the whole world, so wait for the first
    // real size instead. MapInvalidateSizeFix raises Leaflet's resize event once the container is
    // laid out.
    const fitOnceSized = () => {
      if (!hasRenderedSize(map)) return;
      map.off('resize', fitOnceSized);
      fitToBounds();
    };

    map.on('resize', fitOnceSized);
    return () => map.off('resize', fitOnceSized);
  }, [bounds, map, padding, request]);

  return null;
}
